import { CSSProperties, ReactNode } from 'react'
import { Loader, Text, View, ViewProps } from 'reshaped'

export interface ClaimButtonProps extends Omit<ViewProps, 'onClick'> {
  onClick?: () => void
  disabled?: boolean
  loading?: boolean
  text?: string
  textVariant?: 'featured-3' | 'body-2'
  size?: 'default' | 'large'
}

// Border style for the metallic effect
const borderStyle: CSSProperties = {
  background:
    'linear-gradient(97deg, #fff -19.57%, #d2d2d2 18.95%, #fff 57.48%, #d2d2d2 96%, #fff 134.53%)',
  borderRadius: '12px',
  padding: '2px',
  cursor: 'pointer',
  boxShadow: '0px 0px 8px 0px rgba(255, 255, 255, 0.8)',
}

// Background style for the blue button
const blueButtonStyle: CSSProperties = {
  background: 'linear-gradient(180deg, #0085FF 0%, #000AFF 50%, #0085FF 100%)',
  borderRadius: '10px',
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow:
    '-1.472px -1.472px 7.358px 0px #FFF inset, 1.472px 1.472px 7.358px 0px rgba(0, 0, 0, 0.10) inset',
  color: '#FFFFFF',
}

interface StyledButtonProps {
  onClick?: () => void
  disabled?: boolean
  size?: 'default' | 'large'
  children: ReactNode
}

function StyledButton({
  onClick,
  disabled,
  size = 'default',
  children,
}: StyledButtonProps) {
  // Adjust dimensions but keep the same border styling for both sizes
  const dimensions =
    size === 'default'
      ? { height: '48px', width: '90px' }
      : { width: '100%', height: '60px' }

  return (
    <div
      style={{
        ...borderStyle,
        ...dimensions,
      }}
      onClick={disabled ? undefined : onClick}
    >
      <div style={blueButtonStyle}>{children}</div>
    </div>
  )
}

export function ClaimButton({
  onClick,
  disabled = false,
  loading = false,
  text = 'Claim',
  textVariant = 'featured-3',
  size = 'default',
  ...viewProps
}: ClaimButtonProps) {
  return (
    <View {...viewProps}>
      <StyledButton onClick={onClick} disabled={disabled} size={size}>
        <div style={{ color: '#FFFFFF' }}>
          <Text variant={textVariant} weight="semibold">
            {loading ? (
              <Loader size={size === 'large' ? 'medium' : 'small'} color="inherit" />
            ) : (
              text
            )}
          </Text>
        </div>
      </StyledButton>
    </View>
  )
}

export function ClaimedButton({
  textVariant = 'featured-3',
  size = 'default',
  ...viewProps
}: Omit<ClaimButtonProps, 'onClick' | 'loading' | 'disabled' | 'text'>) {
  return (
    <View {...viewProps}>
      <div
        style={{
          height: size === 'default' ? '48px' : '60px',
          width: size === 'default' ? '90px' : '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#F2F2F2',
          borderRadius: '12px',
        }}
      >
        <Text variant={textVariant} weight="semibold" color="neutral-faded">
          Claimed
        </Text>
      </div>
    </View>
  )
}

export function ConnectButton({
  onClick,
  textVariant = 'featured-3',
  size = 'default',
  ...viewProps
}: Omit<ClaimButtonProps, 'loading' | 'disabled' | 'text'>) {
  return (
    <View {...viewProps}>
      <StyledButton onClick={onClick} size={size}>
        <div style={{ color: '#FFFFFF' }}>
          <Text variant={textVariant} weight="semibold">
            Connect
          </Text>
        </div>
      </StyledButton>
    </View>
  )
}
